(ns dre.config
  (:require
   [clojure.string :as str]
   [cljs.reader :as edn]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.network :as-alias network]
   [dre.shared.interface.categories :refer [categories]]
   [dre.util :refer [url-path]]
   [goog.string :as gstring]
   [goog.string.format]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))


(rf/reg-event-fx
 ::init
 [(rf/inject-cofx ::static-config) rf/trim-v]
 (fn-traced
   [{:keys [db static-config]} _]
   {:db (assoc db :static-config static-config)
    :dispatch [::fetch-config]}))

(rf/reg-event-fx
 ::fetch-config
 [rf/trim-v]
 (fn-traced
  [_ _]
  {::network/request [{:endpoint :config/config}
                      {:request-content-type :text
                       :response-content-type :text
                       :timeout 1000
                       :on-success [::fetch-config-ok]
                       :on-failure [::fetch-config-nok]}]}))

(rf/reg-event-fx
 ::fetch-config-ok
 [rf/trim-v]
 (fn-traced
  [{db :db} [{::fetch-fx/keys [response]}]]
  {:db (->> response
            :body
            edn/read-string
            (assoc db :config))
   :dispatch [::fetch-build-info]}))

(rf/reg-event-db
 ::fetch-config-nok
 (fn-traced
  [_ _]
  ; Couldn't fetch build info
  ; Cannot log this to sentry because it is not yet initialized
  nil))

(rf/reg-event-fx
 ::fetch-build-info
 [rf/trim-v]
 (fn-traced
  [_ _]
  {::network/request [{:endpoint :config/build-info}
                      {:request-content-type :text
                       :response-content-type :text
                       :timeout 1000
                       :on-success [::fetch-build-info-ok]
                       :on-failure [::fetch-build-info-nok]}]}))

(rf/reg-event-fx
 ::fetch-build-info-ok
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [{::fetch-fx/keys [response]}]]
  {:db (->> response
            :body
            edn/read-string
            (assoc db :build-info))
   :dispatch [::init-ok]}))

(rf/reg-event-fx
 ::fetch-build-info-nok
 (fn [{:keys [db]} _]
   ; Couldn't fetch build info
   ; Cannot log this to sentry because it is not yet initialized
   ; Sentry won't know the release(commit id) but we should still continue to initialize the app
   {:db (assoc db :build-info :wololoo)
    :dispatch [::init-ok]}))

(rf/reg-event-fx
 ::init-ok
 (fn-traced
  [_ _]
  {:dispatch [:dre.core/init-step-1]}))

(rf/reg-sub
 ::get
 (fn [{:keys [config]} [_ k]]
   (cond
     (nil? k)    config
     (vector? k) (get-in config k)
     :else       (get config k))))

(rf/reg-sub
  ::static-config
  (fn [db _]
    (:static-config db)))

(rf/reg-cofx
  ::static-config
  (fn [cofx]
    (->> js/document.body.dataset.config
         edn/read-string
         (assoc cofx :static-config))))

(defn ->supported-languages
  [db]
  (get-in db [:config :supported-languages]))


(defn ->default-language
  [db]
  (get-in db [:config :default-language]))


(defn ->default-request-timeout-s
  [db]
  (get-in db [:config :default-request-timeout-s]))


(defn ->standard-headers
  [db]
  (get-in db [:config :standard-headers]))



(defn ->api-url
  [db]
  (get-in db [:config :api-url]))

(defn ->sse-api-url
  [db]
  (url-path (get-in db [:config :api-url]) "/api/sse"))

;;sso related stuff
(defn hostname->client-name
  "Check hostname and extract client for SSO redirect.
   E.g.
   sanofi-search.doctorevidence.com
   varian-search.doctorevidence.com
   abbvie-search.doctorevidence.com

   Also medshr.doctorevidence.com

   https://github.com/DoctorEvidence/DocSearch/issues/2389
   "
  [host]
  (cond
    (str/starts-with? host "regintel") nil
    (str/starts-with? host "medshr.") "medshr"
    :else (when-let [idx (str/index-of host "-")] (subs host 0 idx))))

(defn ->sso-url
  [db]
  (let [host (-> js/window
                 .-location
                 .-hostname)
        client (hostname->client-name host)
        base-url (get-in db [:config :sso-base-url])
        login-url (get-in db [:config :sso-url])
        site (get-in db [:config :sso-site])
        return-path  (str (-> js/window
                              .-location
                              .-pathname)
                          (-> js/window
                              .-location
                              .-search))
        sso-url (cond
                  (str/blank? client)
                  (gstring/format "%s/login?redirect=%s%s" login-url base-url return-path)
                  :else
                  (gstring/format "%s/api/federate/loginentry?site=%s&client=%s&ReturnUrl=%s"
                                  login-url site client return-path))]
    sso-url))

(defn ->platforms
  [db]
  (get-in db [:config :platforms]))

;; workaround for accessing config outside of reactive context
(def ^:dynamic global-config
  {})

;; dependency in "/workspace/components/shared/src-cljc/dre/shared/interface/semgroups.cljc"
(defn semgroup-label
  []
  (let [config  global-config]
    (get config :semgroup-label)))

(defn source-labels
  []
  (let [config global-config]
    (get config :source-labels)))

(defn source->count
  [source]
  (let [config global-config]
    (-> config :stats :articles-by-source source)))

(defn dre-user?
  []
  (let [config global-config
        client-id (get-in config [:user-profile :docdata/client-id])]
    (or (= "3" client-id)
        (= "231850759" client-id))))

(defn admin-role?
  []
  (let [config global-config
        roles (get-in config [:user-profile :roles])]
    (contains? (set roles) "docsearchadmin")))

(defn sanofi-csv-export-role?
  []
  (let [config global-config
        roles (get-in config [:user-profile :roles])]
    (contains? (set roles) "sanoficsvexport")))

(defn dev-role?
  []
  (let [config global-config
        roles (-> config :user-profile :roles set)]
    (contains? roles "datahubdev")))

(defn allowed-categories
  []
  (let [config global-config
        allowed-sources (set (get-in config [:user-profile :allowed-sources]))
        client-id (get-in config [:user-profile :docdata/client-id])]
    (filterv
     #(and
       (or (nil? (:source %)) (contains? allowed-sources (:source %)))
       (or (nil? (:client-id %))
           (dev-role?)
           (= (:client-id %) client-id)))
     categories)))


(defn dev-env?
  []
  (let [config global-config
        env (-> config :config :env)]
    (or (= :local env) (= :dev env))))

(defn prod-env?
  []
  (let [config global-config
        env (-> config :config :env)]
    (= :prod env)))

(defn beta-env?
  []
  (let [config global-config
        env (-> config :config :env)]
    (= :beta env)))

(defn feature-enabled?
  [feature-key]
  (let [config global-config 
        features (-> config :config :features)]
    (get features feature-key false)))

;; workaround to include config for dre.common.document-util
(defn config
  []
  (let [config  global-config]
    config))
